import { useState } from 'react';
import CustomAlert from '../CustomAlert';
import { t } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = { name: '', description: '', tax_id: '' };

/**
 * Category form. Renders no page chrome so it can sit either in the Drawer on
 * the list page or standalone on the /categories/add route.
 *
 * onSuccess(created) — drawer closes and the list refreshes
 */
export default function CategoryAdd({ onSuccess }) {
  const [category, setCategory] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ isOpen: false, type: 'error', title: '', message: '' });

  const closeAlert = () => setAlert((a) => ({ ...a, isOpen: false }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCategory((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category.name.trim()) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: t('Недостасува име'),
        message: t('Името на категоријата е задолжително.'),
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/category-add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: category.name.trim(),
          description: category.description,
          tax_id: category.tax_id,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('Категоријата не може да се креира'));

      setCategory(EMPTY);
      if (onSuccess) {
        onSuccess(data);
      } else {
        // Standalone route: no drawer to close and no list to refresh, so this
        // is the only confirmation the user gets.
        setAlert({
          isOpen: true,
          type: 'success',
          title: t('Зачувано'),
          message: t('Категоријата е успешно креирана.'),
        });
      }
    } catch (error) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: t('Грешка'),
        message: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('Назив *')}</label>
          <input
            type="text"
            name="name"
            value={category.name}
            onChange={handleChange}
            required
            className="mt-1 block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('Данок')}</label>
          <select
            name="tax_id"
            value={category.tax_id}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          >
            <option value="">{t('Не е дефиниран')}</option>
            <option value="0">0%</option>
            <option value="5">5%</option>
            <option value="18">18%</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('Опис')}</label>
          <textarea
            name="description"
            rows={4}
            value={category.description}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? t('Се зачувува...') : t('Креирај категорија')}
          </button>
        </div>
      </form>

      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </>
  );
}
